// components/Sidebar.jsx - Mode-specific sidebar with account sections
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';

const Sidebar = ({ onImportClick, onAddTransaction, onManageAccounts }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine current mode
  const budgetingPaths = ['/transactions', '/debts', '/budget', '/bills'];
  const isBudgetingMode = budgetingPaths.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    if (isBudgetingMode) {
      fetchAccountsAndDebts();
    }
  }, [isBudgetingMode]);

  const fetchAccountsAndDebts = async () => {
    try {
      const [accountsRes, debtsRes] = await Promise.all([
        axios.get(`${API_BASE_PATH}/accounts`),
        axios.get(`${API_BASE_PATH}/debts`)
      ]);
      setAccounts((accountsRes.data || []).filter(a => a.is_active));
      setDebts(debtsRes.data || []);
    } catch (error) {
      console.error('Error fetching accounts/debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };

  const handleAccountClick = (accountId) => {
    navigate(`/transactions?account=${accountId}`);
  };

  // Get the selected account from URL
  const searchParams = new URLSearchParams(location.search);
  const selectedAccountId = searchParams.get('account');

  // Separate accounts by type
  const creditCards = debts.filter(d => d.type === 'credit_card');
  const loans = debts.filter(d => d.type !== 'credit_card');
  const bankAccounts = accounts.filter(a => !debts.some(d => d.id === a.id));

  if (isBudgetingMode) {
    return (
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Toggle Button */}
        <button className="sidebar-toggle" onClick={toggleSidebar} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <i className="fas fa-chevron-left"></i>
        </button>

        {/* Action Buttons */}
        <div className="sidebar-actions">
          <button
            onClick={onManageAccounts}
            className="btn-sidebar-action"
            title="Manage Accounts"
          >
            <i className="fas fa-university"></i>
            <span>Manage Accounts</span>
          </button>
        </div>

        {/* Page Navigation */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">
            <span>Pages</span>
          </div>
          <nav className="sidebar-nav">
            <Link
              to="/transactions"
              className={`sidebar-nav-item ${location.pathname === '/transactions' && !location.search ? 'active' : ''}`}
              title={collapsed ? 'All Transactions' : ''}
            >
              <i className="fas fa-receipt"></i>
              <span>All Transactions</span>
            </Link>
            <Link
              to="/debts"
              className={`sidebar-nav-item ${location.pathname === '/debts' ? 'active' : ''}`}
              title={collapsed ? 'Debts' : ''}
            >
              <i className="fas fa-credit-card"></i>
              <span>Debts</span>
            </Link>
            <Link
              to="/budget"
              className={`sidebar-nav-item ${location.pathname === '/budget' ? 'active' : ''}`}
              title={collapsed ? 'Budget' : ''}
            >
              <i className="fas fa-wallet"></i>
              <span>Budget</span>
            </Link>
            <Link
              to="/bills"
              className={`sidebar-nav-item ${location.pathname === '/bills' ? 'active' : ''}`}
              title={collapsed ? 'Bills' : ''}
            >
              <i className="fas fa-file-invoice-dollar"></i>
              <span>Bills</span>
            </Link>
          </nav>
        </div>

        {/* Accounts Section */}
        {!loading && (
          <>
            {bankAccounts.length > 0 && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">
                  <i className="fas fa-university"></i>
                  <span>Accounts</span>
                </div>
                <div className="sidebar-account-list">
                  {bankAccounts.map(account => (
                    <button
                      key={account.id}
                      onClick={() => handleAccountClick(account.id)}
                      className={`sidebar-account-item ${selectedAccountId === account.id ? 'active' : ''}`}
                      title={collapsed ? account.name : ''}
                    >
                      <i className="fas fa-circle"></i>
                      <span>{account.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {creditCards.length > 0 && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">
                  <i className="fas fa-credit-card"></i>
                  <span>Credit Cards</span>
                </div>
                <div className="sidebar-account-list">
                  {creditCards.map(card => (
                    <button
                      key={card.id}
                      onClick={() => handleAccountClick(card.id)}
                      className={`sidebar-account-item ${selectedAccountId === card.id ? 'active' : ''}`}
                      title={collapsed ? card.name : ''}
                    >
                      <i className="fas fa-circle"></i>
                      <span>{card.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loans.length > 0 && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">
                  <i className="fas fa-home"></i>
                  <span>Loans</span>
                </div>
                <div className="sidebar-account-list">
                  {loans.map(loan => (
                    <button
                      key={loan.id}
                      onClick={() => handleAccountClick(loan.id)}
                      className={`sidebar-account-item ${selectedAccountId === loan.id ? 'active' : ''}`}
                      title={collapsed ? loan.name : ''}
                    >
                      <i className="fas fa-circle"></i>
                      <span>{loan.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Trading Mode Sidebar
  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Toggle Button */}
      <button className="sidebar-toggle" onClick={toggleSidebar} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
        <i className="fas fa-chevron-left"></i>
      </button>

      {/* Action Buttons */}
      <div className="sidebar-actions">
        <button
          onClick={onImportClick}
          className="btn-sidebar-action"
          title="Import Orders"
        >
          <i className="fas fa-file-import"></i>
          <span>Import Orders</span>
        </button>
      </div>

      {/* Page Navigation */}
      <div className="sidebar-section">
        <div className="sidebar-section-title">
          <span>Pages</span>
        </div>
        <nav className="sidebar-nav">
          <Link
            to="/"
            className={`sidebar-nav-item ${location.pathname === '/' ? 'active' : ''}`}
            title={collapsed ? 'Dashboard' : ''}
          >
            <i className="fas fa-chart-line"></i>
            <span>Dashboard</span>
          </Link>
          <Link
            to="/trades"
            className={`sidebar-nav-item ${location.pathname === '/trades' ? 'active' : ''}`}
            title={collapsed ? 'Trades' : ''}
          >
            <i className="fas fa-exchange-alt"></i>
            <span>Trades</span>
          </Link>
          <Link
            to="/orders"
            className={`sidebar-nav-item ${location.pathname === '/orders' ? 'active' : ''}`}
            title={collapsed ? 'Orders' : ''}
          >
            <i className="fas fa-list-ul"></i>
            <span>Orders</span>
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;